import { useContext, useEffect, useState } from "react";

import {
	FINCARAIZ_BLOG_CATEGORIES,
	FINCARAIZ_POSTS_PER_CATEGORY,
	FINCARAIZ_WP_REST_BASE,
	FincaraizBlogCategory,
} from "./FincaraizNewsSection.config";
import { ConfigStateContext } from "../../../Contexts/Configurations/context";

export type FincaraizNewsPost = {
	id: string | number;
	title: string;
	slug: string;
	description?: string;
	created: string;
	image: string;
	url?: string;
};

export type FincaraizNewsSlot = {
	category: FincaraizBlogCategory;
	post: FincaraizNewsPost | null;
};

type WpPost = {
	id: number;
	slug: string;
	link?: string;
	date: string;
	title?: { rendered?: string };
	excerpt?: { rendered?: string };
	_embedded?: {
		"wp:featuredmedia"?: Array<{ source_url?: string }>;
	};
};

const stripHtml = (html?: string) => {
	if (!html) return "";
	return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
};

const normalizeWpPost = (post: WpPost): FincaraizNewsPost | null => {
	if (!post?.slug || !post?.title?.rendered) return null;

	const image = post._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
	if (!image) return null;

	return {
		id: post.id,
		title: stripHtml(post.title.rendered),
		slug: post.slug,
		description: stripHtml(post.excerpt?.rendered) || undefined,
		created: post.date,
		image,
		url: post.link?.trim() || undefined,
	};
};

const getPostDedupKey = (post: FincaraizNewsPost): string => {
	if (post.id != null && post.id !== "") return `id:${post.id}`;
	if (post.slug) return `slug:${post.slug}`;
	return `title-date:${post.title}|${post.created}`;
};

const pickFirstUniquePost = (
	posts: FincaraizNewsPost[],
	usedKeys: Set<string>
): FincaraizNewsPost | null => {
	for (const post of posts) {
		const key = getPostDedupKey(post);
		if (usedKeys.has(key)) continue;
		usedKeys.add(key);
		return post;
	}
	return null;
};

const fetchCategoryPosts = async (category: FincaraizBlogCategory): Promise<FincaraizNewsPost[]> => {
	const params = new URLSearchParams({
		categories: String(category.wpCategoryId),
		per_page: String(FINCARAIZ_POSTS_PER_CATEGORY),
		_embed: "wp:featuredmedia",
	});

	const response = await fetch(`${FINCARAIZ_WP_REST_BASE}/posts?${params.toString()}`);
	if (!response.ok) return [];

	const posts: WpPost[] = await response.json();
	if (!Array.isArray(posts) || posts.length === 0) return [];

	return posts
		.map(normalizeWpPost)
		.filter((post): post is FincaraizNewsPost => post !== null);
};

const buildSlotsFromResults = (
	results: PromiseSettledResult<{ category: FincaraizBlogCategory; posts: FincaraizNewsPost[] }>[]
): FincaraizNewsSlot[] => {
	const usedKeys = new Set<string>();

	return FINCARAIZ_BLOG_CATEGORIES.map((category, index) => {
		const result = results[index];
		const posts = result.status === "fulfilled" ? result.value.posts : [];
		const post = pickFirstUniquePost(posts, usedKeys);

		return { category, post };
	});
};

export const useFincaraizNewsSection = () => {
	const { country_code } = useContext(ConfigStateContext);
	const [loading, setLoading] = useState(country_code === "CO");
	const [slots, setSlots] = useState<FincaraizNewsSlot[]>(
		FINCARAIZ_BLOG_CATEGORIES.map(category => ({ category, post: null }))
	);

	useEffect(() => {
		if (country_code !== "CO") return;

		let cancelled = false;

		const loadPosts = async () => {
			setLoading(true);

			const results = await Promise.allSettled(
				FINCARAIZ_BLOG_CATEGORIES.map(async category => ({
					category,
					posts: await fetchCategoryPosts(category),
				}))
			);

			if (cancelled) return;

			setSlots(buildSlotsFromResults(results));
			setLoading(false);
		};

		loadPosts();

		return () => {
			cancelled = true;
		};
	}, [country_code]);

	const hasAllPosts = slots.every(slot => slot.post !== null);

	return { loading, slots, hasAllPosts };
};
